import { useMemo } from 'react';
import { Alert, Form, Grid, SIZE, Stack, Text } from '@quinyx/ui';
import * as z from 'zod';

import { ILicense } from '../../api/interfaces/customer';
import {
  useCreateLicense,
  useGetLicense,
  useUpdateLicense,
} from '@/hooks/customer';
import { useTranslate } from '../../hooks/translate';

type QuinyxLicenseProps = {
  domainId: string;
  isDisabled?: boolean;
};

const EMPTY_LICENSE: ILicense = {
  licenseBasedOn: 'units',
  numberOfLicenses: 0,
  numberOfNonBillableLicenses: 0,
  nonBillableLicensesComment: '',
};

const schema = z.object({
  licenseBasedOn: z.enum(['units', 'users']),
  numberOfLicenses: z.coerce.number().min(0),
  numberOfNonBillableLicenses: z.coerce.number().min(0),
  nonBillableLicensesComment: z.string().optional(),
});

const QuinyxLicense = ({
  domainId,
  isDisabled = false,
}: QuinyxLicenseProps) => {
  const { t } = useTranslate();
  const { data: license } = useGetLicense(domainId);
  const { createLicense } = useCreateLicense();
  const { updateLicense } = useUpdateLicense();

  const licenseBasedOnOptions = useMemo(
    () => [
      { label: t('license.basedOnUnits'), value: 'units' as const },
      { label: t('license.basedOnUsers'), value: 'users' as const },
    ],
    [t],
  );

  const defaultValues = license ?? EMPTY_LICENSE;

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    const payload: ILicense = {
      ...values,
      nonBillableLicensesComment: values.nonBillableLicensesComment ?? '',
    };

    if (license) {
      await updateLicense([domainId, payload]);
    } else {
      await createLicense([domainId, payload]);
    }
  };

  return (
    <Form
      key={license?.domainId}
      onSubmit={handleSubmit}
      schema={schema}
      defaultValues={defaultValues}>
      <Stack spacing={SIZE.L}>
        <Stack spacing={SIZE.XS}>
          <Text variant="headlineHeavy">{t('license.quinyxLicense')}</Text>
          <Text variant="smallRegular" color="SurfaceOnSurfaceAlt">
            {t('license.quinyxDescription')}
          </Text>
        </Stack>

        <Alert type="info">
          <Text variant="smallRegular">{t('license.notCopiedFromProd')}</Text>
        </Alert>

        <Grid columns={[1, 1, 1, 2]} gap={SIZE.M}>
          <Form.Combobox
            data-test-id="license-based-on-combobox"
            name="licenseBasedOn"
            label={t('license.basedOn')}
            info={t('license.basedOnInfo')}
            allowFiltering
            options={licenseBasedOnOptions.map(({ label, value }) => ({
              label,
              value,
            }))}
            required
            disabled={isDisabled}
          />
          <Form.TextInput
            data-test-id="number-of-contracted-licenses-text-input"
            type="number"
            name="numberOfLicenses"
            label={t('license.numberOfContractedLicenses')}
            required
            disabled={isDisabled}
          />
        </Grid>

        <Stack spacing={SIZE.XS}>
          <Grid columns={[1, 1, 2]} gap={SIZE.M}>
            <Form.TextInput
              data-test-id="number-of-non-billable-licenses-text-input"
              type="number"
              name="numberOfNonBillableLicenses"
              label={t('license.numberOfNonBillableLicenses')}
              required
              disabled={isDisabled}
            />
            <Form.TextInput
              data-test-id="non-billable-licenses-comment-text-input"
              name="nonBillableLicensesComment"
              label={t('license.comment')}
              placeholder={t('license.optionalComment')}
              disabled={isDisabled}
            />
          </Grid>
        </Stack>

        <Stack alignItems="flex-end">
          <Form.SubmitButton
            data-test-id="update-license-button"
            text={
              license
                ? t('license.updateQuinyxLicense')
                : t('license.createQuinyxLicense')
            }
            disabled={isDisabled}
          />
        </Stack>
      </Stack>
    </Form>
  );
};

export default QuinyxLicense;
